import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, Calendar, Download, 
  ArrowUpRight, ArrowDownRight, Activity 
} from 'lucide-react';
import { format, subDays, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, eachMonthOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useCurrency } from '@/hooks/useCurrency';
import { useVendorAccess } from '@/hooks/useVendorAccess';
import { formatPriceWithCurrency, getCurrencySymbol } from '@/utils/currency';

interface VendorStats {
  totalEarnings: number;
  totalCommissions: number;
  avgOrderValue: number;
  orderCount: number;
  growthRate: number;
}

interface ChartData {
  date: string;
  earnings: number;
  commissions: number;
  orders: number;
}

export default function VendorFinancialDashboard() {
  const [period, setPeriod] = useState<'7days' | '30days' | '3months' | '12months'>('30days');
  const [stats, setStats] = useState<VendorStats | null>(null);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [categoryData, setCategoryData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { displayCurrency, convertPrice } = useCurrency();
  const { vendorIds, loading: accessLoading } = useVendorAccess();

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

  useEffect(() => {
    if (!accessLoading && vendorIds.length > 0) {
      fetchDashboardData();
    }
  }, [period, displayCurrency, accessLoading, vendorIds]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      if (vendorIds.length === 0) return;

      const startDate = getStartDate(period);

      // Fetch transactions
      const { data: transactions, error } = await supabase
        .from('vendor_transactions')
        .select(`
          *,
          orders:order_id (
            created_at,
            products:product_id (
              category_id,
              categories (name)
            )
          )
        `)
        .in('vendor_id', vendorIds)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Helper function to convert amount to display currency
      const convertToDisplay = (amountCents: number, fromCurrency: string) => {
        return convertPrice(amountCents, fromCurrency || 'CDF', displayCurrency);
      };

      // Calculate stats with currency conversion
      const totalEarnings = transactions?.reduce((sum, t) => {
        if (t.type === 'credit') {
          return sum + convertToDisplay(t.net_amount_cents, t.currency);
        }
        return sum;
      }, 0) || 0;
      
      const totalCommissions = transactions?.reduce((sum, t) => {
        return sum + convertToDisplay(t.commission_amount_cents || 0, t.currency);
      }, 0) || 0;
      
      const orderCount = new Set(transactions?.map(t => t.order_id).filter(Boolean)).size;
      const avgOrderValue = orderCount > 0 ? totalEarnings / orderCount : 0;

      // Calculate growth rate with currency conversion
      const midPoint = new Date((startDate.getTime() + Date.now()) / 2);
      const firstHalf = transactions?.filter(t => new Date(t.created_at) < midPoint).reduce((sum, t) => {
        if (t.type === 'credit') {
          return sum + convertToDisplay(t.net_amount_cents, t.currency);
        }
        return sum;
      }, 0) || 0;
      const secondHalf = transactions?.filter(t => new Date(t.created_at) >= midPoint).reduce((sum, t) => {
        if (t.type === 'credit') {
          return sum + convertToDisplay(t.net_amount_cents, t.currency);
        }
        return sum;
      }, 0) || 0;
      const growthRate = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0;

      setStats({
        totalEarnings,
        totalCommissions,
        avgOrderValue,
        orderCount,
        growthRate,
      });

      // Prepare chart data with currency conversion
      const dates = getDatesInPeriod(period);
      const dataByDate = new Map<string, { earnings: number; commissions: number; orders: Set<string> }>();

      dates.forEach(date => {
        dataByDate.set(date, { earnings: 0, commissions: 0, orders: new Set() });
      });

      transactions?.forEach(t => {
        const dateKey = format(new Date(t.created_at), period === '12months' ? 'yyyy-MM' : 'yyyy-MM-dd');
        const existing = dataByDate.get(dateKey);
        if (existing) {
          if (t.type === 'credit') {
            existing.earnings += convertToDisplay(t.net_amount_cents, t.currency);
          }
          existing.commissions += convertToDisplay(t.commission_amount_cents || 0, t.currency);
          if (t.order_id) {
            existing.orders.add(t.order_id);
          }
        }
      });

      const chartData: ChartData[] = Array.from(dataByDate.entries()).map(([date, data]) => ({
        date,
        earnings: data.earnings,
        commissions: data.commissions,
        orders: data.orders.size,
      }));

      setChartData(chartData);

      // Category breakdown with currency conversion
      const categoryMap = new Map<string, number>();
      transactions?.forEach(t => {
        if (t.type === 'credit' && t.orders?.products?.categories?.name) {
          const category = t.orders.products.categories.name;
          categoryMap.set(category, (categoryMap.get(category) || 0) + convertToDisplay(t.net_amount_cents, t.currency));
        }
      });

      const categoryData = Array.from(categoryMap.entries())
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 6);

      setCategoryData(categoryData);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStartDate = (period: string): Date => {
    switch (period) {
      case '7days':
        return subDays(new Date(), 7);
      case '30days':
        return subDays(new Date(), 30);
      case '3months':
        return subMonths(new Date(), 3);
      case '12months':
        return subMonths(new Date(), 12);
      default:
        return subDays(new Date(), 30);
    }
  };

  const getDatesInPeriod = (period: string): string[] => {
    const start = getStartDate(period);
    const end = new Date();

    if (period === '12months') {
      return eachMonthOfInterval({ start, end }).map(d => format(d, 'yyyy-MM'));
    } else {
      return eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd'));
    }
  };

  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: displayCurrency,
    }).format(cents / 100);
  };

  const currencySymbol = getCurrencySymbol(displayCurrency);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ['Dashboard Financier Vendeur', ''],
      ['Période', period],
      ['', ''],
      ['Total Gains', formatAmount(stats?.totalEarnings || 0)],
      ['Total Commissions', formatAmount(stats?.totalCommissions || 0)],
      ['Valeur Moyenne Commande', formatAmount(stats?.avgOrderValue || 0)],
      ['Nombre de Commandes', stats?.orderCount || 0],
      ['Taux de Croissance', `${stats?.growthRate.toFixed(2)}%`],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws1, 'Résumé');

    // Chart data sheet
    const chartDataFormatted = chartData.map(d => ({
      Date: d.date,
      'Gains (€)': (d.earnings / 100).toFixed(2),
      'Commissions (€)': (d.commissions / 100).toFixed(2),
      'Commandes': d.orders,
    }));
    const ws2 = XLSX.utils.json_to_sheet(chartDataFormatted);
    XLSX.utils.book_append_sheet(wb, ws2, 'Données');

    // Category data sheet
    if (categoryData.length > 0) {
      const categoryFormatted = categoryData.map(c => ({
        Catégorie: c.name,
        'Montant (€)': (c.value / 100).toFixed(2),
      }));
      const ws3 = XLSX.utils.json_to_sheet(categoryFormatted);
      XLSX.utils.book_append_sheet(wb, ws3, 'Catégories');
    }

    XLSX.writeFile(wb, `rapport_financier_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('Dashboard Financier Vendeur', 14, 22);
    
    // Period
    doc.setFontSize(12);
    doc.text(`Période: ${period}`, 14, 32);
    
    // Stats
    doc.setFontSize(10);
    let y = 45;
    doc.text(`Total Gains: ${formatAmount(stats?.totalEarnings || 0)}`, 14, y);
    y += 7;
    doc.text(`Total Commissions: ${formatAmount(stats?.totalCommissions || 0)}`, 14, y);
    y += 7;
    doc.text(`Valeur Moyenne Commande: ${formatAmount(stats?.avgOrderValue || 0)}`, 14, y);
    y += 7;
    doc.text(`Nombre de Commandes: ${stats?.orderCount || 0}`, 14, y);
    y += 7;
    doc.text(`Taux de Croissance: ${stats?.growthRate.toFixed(2)}%`, 14, y);
    
    // Table
    const tableData = chartData.slice(-10).map(d => [
      d.date,
      formatAmount(d.earnings),
      formatAmount(d.commissions),
      d.orders.toString(),
    ]);

    (doc as any).autoTable({
      startY: y + 15,
      head: [['Date', 'Gains', 'Commissions', 'Commandes']],
      body: tableData,
    });

    doc.save(`rapport_financier_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Dashboard Financier</h2>
          <p className="text-muted-foreground">
            Analyse détaillée de vos performances
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={(v: any) => setPeriod(v)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">7 derniers jours</SelectItem>
              <SelectItem value="30days">30 derniers jours</SelectItem>
              <SelectItem value="3months">3 derniers mois</SelectItem>
              <SelectItem value="12months">12 derniers mois</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={exportToExcel}>
            <Download className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={exportToPDF}>
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Gains</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatAmount(stats?.totalEarnings || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {stats && stats.growthRate > 0 ? (
                <span className="text-green-600 flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  +{stats.growthRate.toFixed(1)}% vs période précédente
                </span>
              ) : (
                <span className="text-red-600 flex items-center">
                  <TrendingDown className="h-3 w-3 mr-1" />
                  {stats?.growthRate.toFixed(1)}% vs période précédente
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Commissions Payées</CardTitle>
            <ArrowDownRight className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatAmount(stats?.totalCommissions || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {stats && stats.totalEarnings > 0 
                ? `${((stats.totalCommissions / (stats.totalEarnings + stats.totalCommissions)) * 100).toFixed(1)}% du total`
                : 'Aucune commission'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valeur Moy. Commande</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatAmount(stats?.avgOrderValue || 0)}</div>
            <p className="text-xs text-muted-foreground">
              Par commande
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Nombre de Commandes</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.orderCount || 0}</div>
            <p className="text-xs text-muted-foreground">
              Sur la période sélectionnée
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="earnings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="earnings">Évolution des Gains</TabsTrigger>
          <TabsTrigger value="orders">Commandes</TabsTrigger>
          <TabsTrigger value="categories">Par Catégorie</TabsTrigger>
        </TabsList>

        <TabsContent value="earnings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Évolution des Gains et Commissions</CardTitle>
              <CardDescription>
                Suivi quotidien de vos revenus
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => period === '12months' ? format(new Date(value + '-01'), 'MMM yyyy', { locale: fr }) : format(new Date(value), 'dd MMM', { locale: fr })}
                  />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `${(value / 100).toFixed(0)}${currencySymbol}`} />
                  <Tooltip 
                    formatter={(value: number) => formatAmount(value)}
                    labelFormatter={(label) => period === '12months' ? format(new Date(label + '-01'), 'MMMM yyyy', { locale: fr }) : format(new Date(label), 'dd MMMM yyyy', { locale: fr })}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="earnings" stroke="#8884d8" name="Gains" strokeWidth={2} />
                  <Line type="monotone" dataKey="commissions" stroke="#ff8042" name="Commissions" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Volume de Commandes</CardTitle>
              <CardDescription>
                Nombre de commandes par période
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => period === '12months' ? format(new Date(value + '-01'), 'MMM', { locale: fr }) : format(new Date(value), 'dd MMM', { locale: fr })}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    labelFormatter={(label) => period === '12months' ? format(new Date(label + '-01'), 'MMMM yyyy', { locale: fr }) : format(new Date(label), 'dd MMMM yyyy', { locale: fr })}
                  />
                  <Legend />
                  <Bar dataKey="orders" fill="#82ca9d" name="Commandes" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Répartition par Catégorie</CardTitle>
              <CardDescription>
                Vos meilleures catégories de produits
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatAmount(value)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {categoryData.map((cat, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-sm font-medium">{cat.name}</span>
                    </div>
                    <span className="text-sm font-bold">{formatAmount(cat.value)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
